/**
 * Componente CorporateDialog - Diálogo Corporativo Reutilizable
 * Header con gradiente, iconos y mejor jerarquía visual
 * ESTÁNDAR: Usar en toda la aplicación para diálogos y modales
 */

import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  useTheme,
  IconButton,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { DialogProps } from '@mui/material/Dialog';

interface CorporateDialogProps extends Omit<DialogProps, 'title'> {
  /**
   * Título del diálogo
   */
  title: string;
  
  /**
   * Icono a mostrar en el header (opcional)
   */
  icon?: React.ReactElement;
  
  /**
   * Si es true, aplica gradiente en el header
   */
  gradientHeader?: boolean;
  
  /**
   * Contenido del diálogo
   */
  children: React.ReactNode;
  
  /**
   * Acciones del diálogo (botones)
   */
  actions?: React.ReactNode;
  
  /**
   * Si es true, muestra el botón de cerrar
   */
  showCloseButton?: boolean;
  
  /**
   * Función a ejecutar al cerrar
   */
  onClose: () => void;
}

export const CorporateDialog: React.FC<CorporateDialogProps> = ({
  title,
  icon,
  gradientHeader = true,
  children,
  actions,
  showCloseButton = true,
  onClose,
  ...dialogProps
}) => {
  const theme = useTheme();
  
  return (
    <Dialog
      {...dialogProps}
      onClose={onClose}
      PaperProps={{
        sx: {
          borderRadius: 2,
          minWidth: 320,
        },
      }}
    >
      <DialogTitle
        sx={{
          background: gradientHeader
            ? theme.custom.gradients.primary
            : theme.palette.background.paper,
          color: gradientHeader ? theme.palette.primary.contrastText : theme.palette.text.primary,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          py: 2,
          px: 3,
          position: 'relative',
          zIndex: 1, // Mantener el header por encima del contenido
          borderBottom: gradientHeader
            ? 'none'
            : `1px solid ${theme.palette.divider}`,
        }}
      >
        {icon && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              color: gradientHeader ? theme.palette.primary.contrastText : theme.palette.text.primary,
              '& svg': {
                fontSize: 19.2,
              },
            }}
          >
            {icon}
          </Box>
        )}
        <Typography
          variant="h6"
          component="div"
          sx={{
            flex: 1,
            fontWeight: 600,
            color: gradientHeader ? theme.palette.primary.contrastText : theme.palette.text.primary,
          }}
        >
          {title}
        </Typography>
        {showCloseButton && (
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              color: gradientHeader ? theme.palette.primary.contrastText : theme.palette.text.primary,
              '&:hover': {
                backgroundColor: gradientHeader
                  ? 'rgba(255, 255, 255, 0.1)'
                  : theme.palette.action.hover,
              },
            }}
          >
            <Close />
          </IconButton>
        )}
      </DialogTitle>
      
      <DialogContent
        dividers={false}
        sx={{
          px: 0, // Eliminar padding horizontal para controlarlo con el Box wrapper
          py: 0,
          pt: 0, // Eliminar padding-top por defecto de Material-UI
          mt: 0, // Asegurar que no haya margen negativo por defecto
          position: 'relative',
          overflow: 'visible', // Asegurar que el contenido no se corte
          '&.MuiDialogContent-root': {
            paddingTop: 0, // Forzar padding-top a 0 para sobrescribir estilos por defecto
            paddingBottom: 0,
            paddingLeft: 0,
            paddingRight: 0,
          },
        }}
      >
        <Box
          sx={{
            px: 3,
            py: 3,
            pt: 5, // Padding superior aumentado para separar del título con fondo de color primario
            '& > *:first-of-type': {
              mt: '0 !important', // Eliminar margin-top del primer elemento hijo directo
            },
            // Solo eliminar margin-top del primer Grid container hijo directo, no de los anidados
            '& > .MuiGrid-container:first-of-type': {
              mt: '0 !important',
            },
            // No eliminar márgenes de elementos que los especifiquen explícitamente
            // Los Stack y otros elementos mantendrán su spacing natural
          }}
        >
          {children}
        </Box>
      </DialogContent>
      
      {actions && (
        <DialogActions
          sx={{
            px: 3,
            py: 2,
            borderTop: `1px solid ${theme.palette.divider}`,
            gap: 1,
          }}
        >
          {actions}
        </DialogActions>
      )}
    </Dialog>
  );
};

